/**
 * Client-Onboarding-Schemas.
 *
 * Equal-Treatment-Felder folgen in Phase 2 (Spec v1.1 §5.3.3).
 */
import { z } from 'zod';

// ─── Step 1: Firma ───────────────────────────────────────────────────────────

export const clientStep1Schema = z.object({
  companyName: z.string().trim().max(200).optional(),
  legalForm: z.string().trim().max(50).optional(),
  registeredAddressStreet: z.string().trim().max(200).optional(),
  registeredAddressZip: z.string().trim().max(10).optional(),
  registeredAddressCity: z.string().trim().max(100).optional(),
  hrbNumber: z.string().trim().max(50).optional(),
  districtCourt: z.string().trim().max(100).optional(),
  vatId: z
    .string()
    .trim()
    .regex(/^[A-Z]{2}[A-Z0-9]{2,12}$/i, 'USt-ID-Format ungültig (z. B. DE123456789)')
    .optional()
    .or(z.literal('')),
});

// ─── Step 2: Personen & Billing ──────────────────────────────────────────────

const personEntrySchema = z.object({
  name: z.string().trim().min(1).max(200),
  function: z.string().trim().max(100).optional(),
  email: z.string().trim().email('Ungültige E-Mail').optional().or(z.literal('')),
  phone: z.string().trim().max(30).optional(),
});

export type ClientPerson = z.infer<typeof personEntrySchema>;

export const clientStep2Schema = z.object({
  managingDirectors: z.array(z.object({ name: z.string().trim().min(1).max(200) })).optional(),
  authorizedSignatories: z.array(personEntrySchema).optional(),
  billingEmail: z.string().trim().email('Ungültige E-Mail').optional().or(z.literal('')),
  billingAddressDifferent: z.boolean().optional(),
  billingAddressStreet: z.string().trim().max(200).optional(),
  billingAddressZip: z.string().trim().max(10).optional(),
  billingAddressCity: z.string().trim().max(100).optional(),
  requirePoNumber: z.boolean().optional(),
  requireCostCenter: z.boolean().optional(),
  paymentTermsDays: z.number().int().min(0).max(365).optional(),
  earlyPaymentDiscount: z.string().trim().max(200).optional(),
});

// ─── Step 3: Operational ─────────────────────────────────────────────────────

export const clientStep3Schema = z.object({
  timeTrackingMethod: z.string().trim().max(200).optional(),
  onsiteContacts: z.array(personEntrySchema).optional(),
});

// ─── Combined Upsert ─────────────────────────────────────────────────────────

export const upsertClientSchema = clientStep1Schema
  .merge(clientStep2Schema)
  .merge(clientStep3Schema);

export type UpsertClientInput = z.infer<typeof upsertClientSchema>;

/** Vollständige Staff-Pflege ergänzt interne Notizen zum Self-Service-Contract. */
export const staffUpdateClientSchema = upsertClientSchema.merge(
  z.object({ notes: z.string().trim().max(5000).optional() }),
);
export type StaffUpdateClientInput = z.infer<typeof staffUpdateClientSchema>;

/** Nullable DB-Felder, die Staff durch Leeren des Formularfelds entfernen darf. */
export const STAFF_CLEARABLE_CLIENT_FIELDS = [
  'companyName',
  'legalForm',
  'registeredAddressStreet',
  'registeredAddressZip',
  'registeredAddressCity',
  'hrbNumber',
  'districtCourt',
  'vatId',
  'managingDirectors',
  'authorizedSignatories',
  'billingEmail',
  'billingAddressDifferent',
  'billingAddressStreet',
  'billingAddressZip',
  'billingAddressCity',
  'requirePoNumber',
  'requireCostCenter',
  'paymentTermsDays',
  'earlyPaymentDiscount',
  'timeTrackingMethod',
  'onsiteContacts',
  'notes',
] as const;

export const submitClientRequiredFields = [
  'companyName',
  'legalForm',
  'registeredAddressStreet',
  'registeredAddressZip',
  'registeredAddressCity',
  'vatId',
  'billingEmail',
] as const;
